// src/lib/partnerAttribution.js
//
// Single source of truth for "which partner referred this visitor,"
// client-side. Replaces three independent, near-identical copies of
// `new URLSearchParams(location.search).get('ref')` that used to live in
// CalculatorPage.js, AutonomoCalculatorPage.js, and useTriageForm.js — none
// of which persisted past the current URL, so a referral was silently lost
// the moment a user navigated to a page without ?ref= still in the address
// bar (e.g. clicking from /calculator to /spainsnapshot).
//
// Model: capture once, on first arrival, into a cookie. Every page/hook that
// needs to know "is there an active partner referral" reads the cookie via
// getActivePartnerRef() — never re-parses the URL directly.
//
// First-touch, not last-touch: once a referral is captured, a later page
// load with no ?ref= (or a different one) does NOT overwrite it. This
// mirrors the server-side stickiness enforced in
// _shared/partnerAssignment.ts's assignPartnerIfSafe() — the two layers
// implement the same "first touch wins" rule at two different points
// (browser cookie + database), not because either alone is sufficient.
//
// Cookie is scoped to the current host only (no `domain=.holasam.app`) —
// confirmed with John that partner referral links only ever land inside
// portal.holasam.app, never on the marketing site (holasam.app). Revisit
// this (switch to a parent-domain cookie) if that ever changes.
//
// No client-side validation against the partners table — that's deliberately
// a server-side concern (see resolvePartnerId in
// _shared/partnerAssignment.ts), which tolerates an unknown/stale ref
// gracefully rather than failing. Validating here would mean an extra
// round-trip on every page load for no real benefit; a garbage ref just
// resolves to "no partner" (claim-session) or "default partner"
// (advisor-connect) server-side.

const COOKIE_NAME = 'hs_partner_ref'

// 60 days: long enough to cover a multi-week "let me think about my taxes"
// decision cycle, short enough that attribution doesn't linger indefinitely.
// Single constant — change here only, nothing else needs to know the number.
const COOKIE_MAX_AGE_DAYS = 60

function readCookie(name) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name, value, maxAgeDays) {
  if (typeof document === 'undefined') return
  const maxAgeSeconds = maxAgeDays * 24 * 60 * 60
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`
}

/**
 * Call once, high in the app (AppShell.js), on every load. Reads ?ref= from
 * the current URL and stores it — but ONLY if no partner cookie is already
 * set. This is what makes attribution first-touch: a second, later ?ref=
 * (or a page with none at all) never overwrites an already-captured
 * referral.
 */
export function captureReferralIfPresent() {
  if (typeof window === 'undefined') return

  const ref = new URLSearchParams(window.location.search).get('ref')
  if (!ref) return

  const existing = readCookie(COOKIE_NAME)
  if (existing) return // first-touch: never overwrite an existing referral

  writeCookie(COOKIE_NAME, ref, COOKIE_MAX_AGE_DAYS)
}

/**
 * The single read point every page/hook should use instead of re-parsing
 * location.search directly.
 */
export function getActivePartnerRef() {
  return readCookie(COOKIE_NAME)
}
